/**
 * Shared schema.org Vehicle builder for a rental listing.
 *
 * Used by the listing page (full detail) and by the destinations page's
 * inventory ItemList (summary detail), so the same van is described the same
 * way wherever it appears. Everything comes from data we already hold: the
 * listing's publicData, labelled through the marketplace's listing-field and
 * category config so machines read "Automatic" / "Diesel" / "Sleeps 4", never
 * raw option codes like "-5_years".
 *
 * Deliberate choices (agreed with the client):
 * - `seats_selector` means SLEEPS (berths), so it is published as a "Sleeps"
 *   property — never as vehicleSeatingCapacity, which would claim a seatbelt
 *   count we don't hold.
 * - Pickup location is the listing's address text only. No coordinates: for
 *   private owners the exact pickup point is often their home.
 * - The price is passed in by the caller and must be the exact figure the page
 *   shows (commission-adjusted). No price → no offer, never a raw figure.
 * - Brand, model and model year are not published: there are no listing
 *   fields for them.
 */

// Same campervan/motorhome split as the destinations page's two carousels
// (categoryLevel1 ids from the marketplace category configuration).
const CAMPERVAN_CATEGORY_IDS = new Set(['campervan', 'large-campervan', 'rooftop']);
export const normalizeCategoryId = c =>
  String(c || '')
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
export const isCampervanCategory = c => CAMPERVAN_CATEGORY_IDS.has(normalizeCategoryId(c));

// Marketing tags in the `features` field, not facilities of the vehicle.
// 'Pets' is published through the dedicated "Pet friendly" property instead.
const NON_FACILITY_FEATURES = new Set(['pets', 'familyfav', 'fortwo', 'festival']);

// Hire terms added on the listing page only (full detail), as "Label: Option".
const FULL_DETAIL_ENUM_FIELDS = [
  'vehicleage',
  'minimum_age',
  'LicenceCategory',
  'minimumhire',
  'daily_mileage_limit',
  'extra_miles_charge',
  'festivals_allowed',
  'smoking_vaping',
  'travel_restrictions',
  'cancellationpolicy',
  'pick_up_time',
  'drop_of_time',
];

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

const humanize = value =>
  String(value || '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, ch => ch.toUpperCase());

const fieldConfig = (listingFields, key) =>
  (Array.isArray(listingFields) ? listingFields : []).find(f => f && f.key === key) || null;

const fieldLabel = (listingFields, key) => {
  const f = fieldConfig(listingFields, key);
  const label = f?.showConfig?.label || f?.saveConfig?.label || f?.label;
  return label ? String(label).trim() : humanize(key);
};

// Older listings store some options in a legacy spelling ("island_bed",
// "awning") next to the current one ("islandBed", "Awning"), so options match
// ignoring case and separators.
const optionKey = option => String(option || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const configuredOptionLabel = (listingFields, key, option) => {
  const f = fieldConfig(listingFields, key);
  const match = (f?.enumOptions || []).find(o => optionKey(o.option) === optionKey(option));
  return match?.label ? String(match.label).trim() : null;
};

const optionLabel = (listingFields, key, option) =>
  configuredOptionLabel(listingFields, key, option) || humanize(option);

const findCategoryName = (categories, id) => {
  for (const c of Array.isArray(categories) ? categories : []) {
    if (!c) continue;
    if (normalizeCategoryId(c.id) === normalizeCategoryId(id)) return c.name || null;
    const nested = findCategoryName(c.subcategories, id);
    if (nested) return nested;
  }
  return null;
};

const asList = v => (Array.isArray(v) ? v : v === null || v === undefined || v === '' ? [] : [v]);

// seats_selector is a cumulative multi-select ("1_seat" … "6_seats"); the
// highest option is the capacity.
const sleepsFrom = seatsSelector => {
  const counts = asList(seatsSelector)
    .map(o => parseInt(String(o), 10))
    .filter(n => Number.isFinite(n) && n > 0);
  return counts.length ? Math.max(...counts) : null;
};

const petFriendlyFrom = publicData => {
  if (publicData.pets_allowed === 'pets_permited') return true;
  if (publicData.pets_allowed === 'no_pets_permitted') return false;
  return asList(publicData.features).some(f => optionKey(f) === 'pets') ? true : null;
};

const property = (name, value) => ({ '@type': 'PropertyValue', name, value });

/**
 * Average rating from the review totals ListingCard also uses
 * (publicData.reviewsCount / totalRating). Null until a review exists.
 */
export const ratingFromPublicData = publicData => {
  const count = Number(publicData?.reviewsCount);
  const total = Number(publicData?.totalRating);
  if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(total) || total <= 0) return null;
  return { value: Number((total / count).toFixed(1)), count };
};

/**
 * @param {Object} params
 * @param {string} params.listingId
 * @param {string} [params.name]
 * @param {string} [params.description] - full detail only
 * @param {string} params.url - absolute listing URL
 * @param {string} [params.imageUrl]
 * @param {Object} [params.publicData] - listing publicData
 * @param {{amount: number, currency: string}|null} [params.price] - the price the page shows, minor units
 * @param {{value: number, count: number}|null} [params.rating]
 * @param {string} [params.availability] - schema.org availability URL (listing page)
 * @param {string} [params.marketplaceName] - the seller
 * @param {Array} [params.listingFields] - config.listing.listingFields
 * @param {Array} [params.categories] - config.categoryConfiguration.categories
 * @param {'full'|'summary'} [params.detail]
 * @returns {Object} Vehicle properties, without '@context' / '@type'
 */
export const buildVehicleSchema = ({
  listingId,
  name,
  description,
  url,
  imageUrl,
  publicData: rawPublicData,
  price,
  rating,
  availability,
  marketplaceName,
  listingFields,
  categories,
  detail = 'summary',
}) => {
  const publicData = rawPublicData && typeof rawPublicData === 'object' ? rawPublicData : {};
  const full = detail === 'full';
  const category = asList(publicData.categoryLevel1)[0];
  const address = publicData.location?.address;

  const additionalProperty = [];
  const sleeps = sleepsFrom(publicData.seats_selector);
  if (sleeps) additionalProperty.push(property('Sleeps', sleeps));
  const petFriendly = petFriendlyFrom(publicData);
  if (petFriendly !== null) additionalProperty.push(property('Pet friendly', petFriendly));
  // Only facilities the owner ticked: an unticked box isn't a reliable "no".
  // De-duplicated, since a listing can hold both spellings of one option;
  // the configured label wins over a humanised legacy code.
  const facilities = new Map();
  asList(publicData.features)
    .filter(f => !NON_FACILITY_FEATURES.has(optionKey(f)))
    .forEach(f => {
      const configured = configuredOptionLabel(listingFields, 'features', f);
      const label = configured || humanize(f);
      const existing = facilities.get(optionKey(label));
      if (!existing || (configured && !existing.configured)) {
        facilities.set(optionKey(label), { label, configured: !!configured });
      }
    });
  facilities.forEach(({ label }) => additionalProperty.push(property(label, true)));
  if (full) {
    FULL_DETAIL_ENUM_FIELDS.forEach(key => {
      const value = publicData[key];
      if (value === null || value === undefined || value === '') return;
      additionalProperty.push(
        property(fieldLabel(listingFields, key), optionLabel(listingFields, key, value))
      );
    });
    if (Number.isFinite(publicData.deposit) && publicData.deposit > 0) {
      const currency = price?.currency || 'GBP';
      const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
      additionalProperty.push(property('Security deposit', `${symbol}${Math.round(publicData.deposit / 100)}`));
    }
  }

  const fuels = asList(publicData.fueltype).map(f => optionLabel(listingFields, 'fueltype', f));
  const seller = marketplaceName ? { '@type': 'Organization', name: marketplaceName } : undefined;

  let offers;
  if (price && Number.isFinite(price.amount) && price.amount > 0 && price.currency) {
    // Two decimals: the page shows whole pounds and getCommissionAdjustedPrice
    // rounds to them, so this is the same figure, e.g. "180.00".
    const amount = (price.amount / 100).toFixed(2);
    offers = {
      '@type': 'Offer',
      price: amount,
      priceCurrency: price.currency,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: amount,
        priceCurrency: price.currency,
        unitText: 'night',
        referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'night' },
      },
      url,
      ...(availability && { availability }),
      ...(seller && { seller }),
      ...(address && { availableAtOrFrom: { '@type': 'Place', address } }),
    };
  }

  const schema = {
    name: name || undefined,
    ...(full && description && { description }),
    identifier: listingId,
    url,
    ...(imageUrl && { image: imageUrl }),
    ...(category && {
      bodyType: isCampervanCategory(category) ? 'Campervan' : 'Motorhome',
      vehicleConfiguration: findCategoryName(categories, category) || humanize(category),
    }),
    vehicleSpecialUsage: 'https://schema.org/RentalVehicleUsage',
    ...(publicData.transmission && {
      vehicleTransmission: optionLabel(listingFields, 'transmission', publicData.transmission),
    }),
    ...(fuels.length > 0 && { fuelType: fuels.length === 1 ? fuels[0] : fuels }),
    ...(additionalProperty.length > 0 && { additionalProperty }),
    ...(offers && { offers }),
    ...(rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating.value,
        reviewCount: rating.count,
        bestRating: 5,
      },
    }),
  };
  if (!schema.name) delete schema.name;
  return schema;
};
